import { motion } from 'framer-motion';
import { Helmet } from 'react-helmet-async';
import { Link } from 'react-router-dom';
import { ArrowUpRight, Check } from 'lucide-react';
import PageHeader from '@/components/common/PageHeader';
import Reveal from '@/components/common/Reveal';
import { services } from '@/data/projects';

const engagements = [
  {
    n: 'A',
    title: 'Landing Page',
    price: 'from ₹10,000',
    turnaround: '2 weeks',
    for: 'Single-doctor practices, campaigns, treatment launches.',
    includes: ['One-page design', 'Custom copy direction', 'Enquiry flow to WhatsApp', 'Analytics + basic SEO'],
  },
  {
    n: 'B',
    title: 'Clinic Website',
    price: 'from ₹15,000',
    turnaround: '3–5 weeks',
    for: 'Skin, dental, and aesthetic clinics with 1–3 practitioners.',
    includes: ['5–8 pages, editorial layout', 'Bookings + intake', 'Practitioner pages', 'Search-ready foundations'],
    featured: true,
  },
  {
    n: 'C',
    title: 'Institute Build',
    price: 'on quote',
    turnaround: '6–8 weeks',
    for: 'Multi-doctor institutes, franchises, hospital groups.',
    includes: ['Design system', 'CMS integration', 'Location + department routing', 'Team & careers architecture'],
  },
];

export default function ServicesPage() {
  return (
    <motion.main
      id="main-content"
      initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
      transition={{ duration: 0.5 }}
    >
      <Helmet>
        <title>Services | TechsFlow</title>
      </Helmet>
      <PageHeader
        eyebrow="Index · 03 · Services"
        title="What we make,"
        italicTail="listed plainly."
        lede="Six services, three engagement shapes, one team. No retainer traps, no phased proposals designed to expand mid-project."
      />

      <section className="py-16 md:py-24">
        <div className="mx-auto container-editorial">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {services.map((s, i) => (
              <Reveal key={s.n} delay={i * 0.06}>
                <article className="card-editorial p-8 h-full flex flex-col min-h-[320px] group">
                  <div className="flex items-center justify-between mb-8">
                    <span className="font-mono text-xs text-muted-foreground rule-numeric">— {s.n}</span>
                    <span className="w-2 h-2 rounded-full bg-sienna/30 group-hover:bg-sienna transition-colors" />
                  </div>
                  <h3 className="font-serif text-3xl leading-tight tracking-tight mb-3">{s.title}</h3>
                  <p className="text-sm text-muted-foreground leading-relaxed mb-6">{s.lede}</p>
                  <ul className="mt-auto space-y-2">
                    {s.bullets.map(b => (
                      <li key={b} className="flex items-center gap-2 text-sm">
                        <Check className="w-3.5 h-3.5 text-sienna" />
                        <span>{b}</span>
                      </li>
                    ))}
                  </ul>
                </article>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <section className="py-20 md:py-28 bg-ink text-paper">
        <div className="mx-auto container-editorial">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-16 mb-14">
            <Reveal className="lg:col-span-6">
              <div className="label-eyebrow mb-4 text-paper/60">Engagement shapes</div>
              <h2 className="font-serif text-4xl md:text-5xl lg:text-6xl leading-[0.98] tracking-tight text-paper text-balance">
                Three ways to work <span className="italic-serif text-paper/60 text-sienna">with us.</span>
              </h2>
            </Reveal>
            <Reveal delay={0.15} className="lg:col-span-6 lg:pt-4">
              <p className="text-base md:text-lg text-paper/70 leading-relaxed max-w-lg">
                Every engagement begins with a written brief. Every quote is
                fixed. If it changes mid-project, we'll say so — in writing,
                with a reason.
              </p>
            </Reveal>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 md:gap-6">
            {engagements.map((e, i) => (
              <Reveal key={e.n} delay={i * 0.1}>
                <div className={`rounded-[1.25rem] p-8 h-full flex flex-col min-h-[420px] border ${
                  e.featured
                    ? 'bg-paper text-ink border-paper'
                    : 'bg-transparent text-paper border-paper/20'
                }`}>
                  <div className="flex items-center justify-between mb-8">
                    <span className="font-mono text-xs opacity-70 rule-numeric">Shape {e.n}</span>
                    {e.featured && (
                      <span className="text-[10px] font-mono uppercase tracking-widest px-2.5 py-1 rounded-full bg-sienna text-paper">
                        Most chosen
                      </span>
                    )}
                  </div>
                  <h3 className="font-serif text-3xl md:text-4xl leading-tight tracking-tight mb-2">
                    {e.title}
                  </h3>
                  <p className={`text-sm mb-6 leading-relaxed ${e.featured ? 'text-ink/70' : 'text-paper/70'}`}>
                    {e.for}
                  </p>
                  <div className="grid grid-cols-2 gap-4 mb-6">
                    <div>
                      <div className="text-[10px] font-mono uppercase tracking-widest opacity-60 mb-1">From</div>
                      <div className="font-serif text-2xl">{e.price}</div>
                    </div>
                    <div>
                      <div className="text-[10px] font-mono uppercase tracking-widest opacity-60 mb-1">Turnaround</div>
                      <div className="font-serif text-2xl">{e.turnaround}</div>
                    </div>
                  </div>
                  <ul className="mt-auto space-y-2">
                    {e.includes.map(x => (
                      <li key={x} className="flex items-center gap-2 text-sm">
                        <Check className={`w-3.5 h-3.5 ${e.featured ? 'text-sienna' : 'text-sienna-soft'}`} />
                        <span>{x}</span>
                      </li>
                    ))}
                  </ul>
                  <Link
                    to="/contact"
                    className={`mt-8 inline-flex items-center justify-between gap-2 px-4 py-3 rounded-full text-sm font-medium border transition-colors ${
                      e.featured
                        ? 'bg-ink text-paper border-ink hover:bg-sienna hover:border-sienna'
                        : 'border-paper/25 text-paper hover:bg-paper/10'
                    }`}
                  >
                    Start with Shape {e.n}
                    <ArrowUpRight className="w-4 h-4" />
                  </Link>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>
    </motion.main>
  );
}
